<template>
  <a-spin :spinning="loading">
    <a-steps :current="currentStep" class="mb-5">
      <a-step title="Pilih Konteks" />
      <a-step title="Upload File" />
      <a-step title="Review & Konfirmasi" />
    </a-steps>

    <div class="step-content">
      <div v-if="currentStep === 0">
        <h2 class="text-subtitle">Langkah 1: Pilih Konteks Tujuan Aset</h2>
        <p>
          Semua aset di dalam file Excel yang Anda upload akan dimasukkan ke dalam Customer, Lokasi,
          dan Ruangan yang Anda pilih di bawah ini.
        </p>
        <div class="mt-5">
          <h1 class="text-title">Customer & Location</h1>
        </div>
        <a-row :gutter="[24, 24]">
          <a-col :xs="24" :md="8">
            <p class="text-label">Customer</p>
            <a-select
              v-model:value="selectedCustomerId"
              placeholder="Pilih Customer"
              class="w-100"
              show-search
              :options="customerOptions"
              :filter-option="filterOption"
              @change="handleCustomerChange"
            />
          </a-col>
          <a-col :xs="24" :md="8">
            <p class="text-label">Lokasi</p>
            <a-select
              v-model:value="selectedLocationId"
              placeholder="Pilih Lokasi"
              class="w-100"
              show-search
              :options="locationOptions"
              :filter-option="filterOption"
              :disabled="!selectedCustomerId || locationOptions.length === 0"
              @change="handleLocationChange"
            />
          </a-col>
          <a-col :xs="24" :md="8">
            <p class="text-label">Ruangan</p>
            <a-select
              v-model:value="selectedRoomId"
              placeholder="Pilih Ruangan"
              class="w-100"
              show-search
              :options="roomOptions"
              :filter-option="filterOption"
              :disabled="!selectedLocationId || roomOptions.length === 0"
            />
          </a-col>
        </a-row>
        <div class="mt-5">
          <h1 class="text-title">Asset Information</h1>
        </div>
        <a-row :gutter="[24, 24]">
          <a-col :xs="{ span: 24 }" :sm="{ span: 8 }">
            <div class="w-100">
              <p style="text-align: left;" class="text-label">Asset Category</p>
              <a-select
                placeholder="Pilih Asset Category"
                class="w-100"
                v-model:value="selectedCategoriesAssets"
                allow-clear
                show-search
                :options="assetSubCategoriesOptions"
                :filter-option="filterOption"
              >
              </a-select>
            </div>
          </a-col>
          <a-col :xs="{ span: 24 }" :sm="{ span: 8 }">
            <div class="w-100">
              <p style="text-align: left;" class="text-label">Brand</p>
              <a-select
                placeholder="Pilih Manufacturer"
                class="w-100"
                v-model:value="selectedManufatureId"
                allow-clear
                show-search
                :options="manufactureOptions"
                :filter-option="filterOption"
              >
              </a-select>
            </div>
          </a-col>
          <a-col :xs="{ span: 24 }" :sm="{ span: 8 }">
            <p class="text-label">Vendor</p>
            <a-select
              placeholder="Pilih Vendor"
              class="w-100"
              v-model:value="selectedVendorId"
              allow-clear
              show-search
              :options="vendorOptions"
              :filter-option="filterOption"
            >
            </a-select>
          </a-col>
          <a-col :xs="{ span: 24 }" :sm="{ span: 8 }">
            <p class="text-label">Siklus Monitoring Asset</p>
            <a-select
              placeholder="Pilih Siklus Asset"
              class="w-100"
              v-model:value="selectedCycleId"
            >
              <a-select-option v-for="(item, index) in cycleData" :key="index" :value="item.id">{{
                item.name
              }}</a-select-option>
            </a-select>
          </a-col>
          <br />
        </a-row>

        <div class="d-flex justify-content-end mt-4">
          <a-button type="primary" :disabled="!isContextSelected" @click="nextStep">
            Selanjutnya <RightOutlined />
          </a-button>
        </div>
      </div>

      <div v-if="currentStep === 1">
        <h2 class="text-subtitle">Langkah 2: Siapkan dan Upload File Excel</h2>
        <a-alert
          message="Template Excel sudah disederhanakan. Anda tidak perlu lagi mengisi kolom Customer, Lokasi, dan Ruangan di dalam file."
          type="info"
          show-icon
          class="mb-4"
        />
        <a-row :gutter="[24, 24]" align="middle">
          <a-col :xs="24" :md="12">
            <p>Download template yang sudah disiapkan untuk memastikan semua kolom sesuai.</p>
            <a-button @click="downloadTemplate"> <DownloadOutlined /> Download Template </a-button>
          </a-col>
          <a-col :xs="24" :md="12">
            <p>Upload file Excel yang sudah Anda isi.</p>
            <a-upload-dragger
              name="file"
              :multiple="false"
              :before-upload="handleFile"
              :show-upload-list="false"
              accept=".xlsx, .xls"
            >
              <p class="ant-upload-drag-icon">
                <inbox-outlined></inbox-outlined>
              </p>
              <p class="ant-upload-text">Klik atau seret file ke area ini untuk meng-upload</p>
              <p class="ant-upload-hint">
                Hanya mendukung file .xlsx atau .xls.
              </p>
            </a-upload-dragger>
          </a-col>
        </a-row>
        <div class="d-flex justify-content-start mt-4">
          <a-button @click="prevStep"> <LeftOutlined /> Kembali </a-button>
        </div>
      </div>

      <div v-if="currentStep === 2">
        <h2 class="text-subtitle">Langkah 3: Review dan Konfirmasi Data</h2>
        <a-descriptions
          title="Ringkasan Konteks"
          :column="{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }"
          class="mb-4"
        >
          <a-descriptions-item label="Customer">{{ selectedCustomerName }}</a-descriptions-item>
          <a-descriptions-item label="Lokasi">{{ selectedLocationName }}</a-descriptions-item>
          <a-descriptions-item label="Ruangan">{{ selectedRoomName }}</a-descriptions-item>
          <a-descriptions-item label="Asset Category">{{
            selectedCategoriesAssetsName
          }}</a-descriptions-item>
          <a-descriptions-item label="Brand">{{ selectedManufatureName }}</a-descriptions-item>
          <a-descriptions-item label="Vendor">{{ selectedVendorName }}</a-descriptions-item>
        </a-descriptions>

        <p>
          Anda akan meng-upload <strong>{{ parsedAssets.length }} aset baru</strong>. Mohon periksa
          kembali data di bawah sebelum melanjutkan.
        </p>
        <a-table
          :columns="tableColumns"
          :data-source="parsedAssets"
          :scroll="{ x: 800 }"
          bordered
          class="mt-3"
        >
        </a-table>
        <div class="d-flex justify-content-between mt-4">
          <a-button @click="prevStep"> <LeftOutlined /> Kembali </a-button>
          <a-button type="primary" @click="handleSubmit" :loading="loading">
            Konfirmasi & Submit Data
          </a-button>
        </div>
      </div>
    </div>
  </a-spin>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { notification } from 'ant-design-vue'
import { InboxOutlined, DownloadOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons-vue'
import * as XLSX from 'xlsx'

// --- Impor Service (Gantilah dengan path yang benar) ---
// Anggap saja fungsi-fungsi ini sudah ada dan bisa dipanggil
import {
  getCustomers,
  getLocationByCustomerId,
  getRoomByCustomerId,
  getCycleAssets,
} from '../../services/axios/modc/customer'
import { getVendors } from '../../services/axios/modc/vendor'
import { getManufactures } from '../../services/axios/modc/manufacture'
import { getCategories, postAssetsBulks } from '../../services/axios/modc/assets'

import { useRoute, useRouter } from 'vue-router'
function excelSerialToDateString(serial) {
  if (typeof serial !== 'number' || isNaN(serial)) {
    // Jika data bukan angka (mungkin sudah string atau kosong), kembalikan apa adanya.
    return serial
  }
  // Excel salah menganggap tahun 1900 sebagai tahun kabisat.
  // Perhitungan ini mengompensasi bug tersebut dan timezone.
  const utc_days = Math.floor(serial - 25569)
  const utc_value = utc_days * 86400
  const date_info = new Date(utc_value * 1000)

  const year = date_info.getUTCFullYear()
  const month = String(date_info.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date_info.getUTCDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}
// import { bulkUploadAssets } from '../../services/placeholder-api'

// --- State Management ---
let route = useRoute()

const currentStep = ref(0)
const loading = ref(false)

// State untuk Konteks
const selectedCategoriesAssets = ref()
const selectedCustomerId = ref(null)
const selectedLocationId = ref(null)
const selectedVendorId = ref(null)
const selectedManufatureId = ref()
const selectedCycleId = ref()

const selectedRoomId = ref(null)

// State untuk data dropdown
const customerOptions = ref([])
const locationOptions = ref([])
const roomOptions = ref([])
const dataCategoriesAssets = ref([])
const dataManufatures = ref([])
const dataVendors = ref([])
const cycleData = ref([])

// State untuk data dari Excel
const parsedAssets = ref([])
const tableColumns = ref([
  { title: 'Nama Aset', dataIndex: 'Asset_Name', key: 'Asset_Name', fixed: 'left', width: 200 },
  { title: 'Model', dataIndex: 'Model', key: 'Model', width: 150 },
  { title: 'Product Number', dataIndex: 'Product_Number', key: 'Product_Number', width: 150 },
  { title: 'Serial Number', dataIndex: 'Serial_Number', key: 'Serial_Number', width: 150 },
  { title: 'Service Tag', dataIndex: 'Service_Tag', key: 'Service_Tag', width: 150 },
  { title: 'Asset Tag', dataIndex: 'Asset_Tag', key: 'Asset_Tag', width: 150 },
  { title: 'Rack No', dataIndex: 'Rack_No', key: 'Rack_No', width: 100 },
  { title: 'U No', dataIndex: 'U_No', key: 'U_No', width: 100 },
  { title: 'U Size', dataIndex: 'U_Size', key: 'U_Size', width: 100 },
  {
    title: 'Tgl Pembelian',
    dataIndex: 'Procurement_Tanggal_Pembelian',
    key: 'Procurement_Tanggal_Pembelian',
    width: 150,
  },
  {
    title: 'No Pembelian',
    dataIndex: 'Procurement_Nomor_Pembelian',
    key: 'Procurement_Nomor_Pembelian',
    width: 150,
  },
  {
    title: 'Harga',
    dataIndex: 'Procurement_Harga',
    key: 'Procurement_Harga',
    width: 120,
    align: 'right',
  },
  {
    title: 'Garansi (Bulan)',
    dataIndex: 'Procurement_Garansi',
    key: 'Procurement_Garansi',
    width: 120,
  },
  {
    title: 'Garansi Mulai',
    dataIndex: 'Procurement_Garansi_Mulai',
    key: 'Procurement_Garansi_Mulai',
    width: 150,
  },
  {
    title: 'Garansi Berakhir',
    dataIndex: 'Procurement_Garansi_Berakhir',
    key: 'Procurement_Garansi_Berakhir',
    width: 150,
  },
  { title: 'Deskripsi', dataIndex: 'Description', key: 'Description', width: 250 },
])

// --- Computed Properties ---
const isContextSelected = computed(() => {
  return (
    selectedCustomerId.value &&
    selectedLocationId.value &&
    selectedRoomId.value &&
    selectedCategoriesAssets.value &&
    selectedManufatureId.value &&
    selectedCycleId.value
  )
})
const assetSubCategoriesOptions = computed(() => {
  const defaultOption = { value: '', label: 'Pilih Asset Category' }

  return [
    defaultOption,
    ...dataCategoriesAssets.value.map(item => ({
      value: item.id,
      label: item.name,
    })),
  ]
})
const manufactureOptions = computed(() => {
  const defaultOption = { value: '', label: 'Pilih Ruangan' }

  return [
    defaultOption,
    ...dataManufatures.value.map(item => ({
      value: item.id,
      label: item.name,
    })),
  ]
})

const vendorOptions = computed(() => {
  const defaultOption = { value: '', label: 'Pilih Vendor' }

  return [
    defaultOption,
    ...dataVendors.value.map(item => ({
      value: item.id,
      label: item.name,
      ...item,
    })),
  ]
})

const selectedCustomerName = computed(() => {
  const selected = customerOptions.value.find(opt => opt.value === selectedCustomerId.value)
  return selected ? selected.label : ''
})

const selectedLocationName = computed(() => {
  const selected = locationOptions.value.find(opt => opt.value === selectedLocationId.value)
  return selected ? selected.label : ''
})

const selectedRoomName = computed(() => {
  const selected = roomOptions.value.find(opt => opt.value === selectedRoomId.value)
  return selected ? selected.label : ''
})

const selectedCategoriesAssetsName = computed(() => {
  const selected = assetSubCategoriesOptions.value.find(
    opt => opt.value === selectedCategoriesAssets.value,
  )
  return selected ? selected.label : ''
})

const selectedManufatureName = computed(() => {
  const selected = manufactureOptions.value.find(opt => opt.value === selectedManufatureId.value)
  return selected ? selected.label : ''
})

const selectedVendorName = computed(() => {
  const selected = vendorOptions.value.find(opt => opt.value === selectedVendorId.value)
  return selected ? selected.label : ''
})

// --- Lifecycle Hook ---
onMounted(() => {
  selectedCategoriesAssets.value = +route.query.subCategory
  fetchData()
})

// --- Functions ---
const nextStep = () => {
  if (currentStep.value < 2) currentStep.value++
}

const prevStep = () => {
  if (currentStep.value > 0) currentStep.value--
}

const fetchData = async () => {
  console.log('masuksinibaruuu')
  loading.value = true
  try {
    // Panggil API untuk mendapatkan customer
    const { customer } = await getCustomers()
    const { categories } = await getCategories()
    const { cycle } = await getCycleAssets()
    console.log(cycle, 'inicycle')

    const { manufacturers } = await getManufactures()
    const { vendor } = await getVendors()

    dataVendors.value = vendor.map(e => ({ name: e.name, id: e.id, ...e }))
    customerOptions.value = customer.map(c => ({ value: c.id, label: c.name }))
    dataCategoriesAssets.value = categories.map(e => ({ name: e.name, id: e.id }))
    dataManufatures.value = manufacturers.map(e => ({ name: e.name, id: e.id }))
    cycleData.value = cycle
  } catch (error) {
    console.log(error)
    notification.error({ message: 'Gagal memuat data customer' })
  } finally {
    loading.value = false
  }
}

const handleCustomerChange = async customerId => {
  console.log(customerId, 'cekini')
  // Reset pilihan di bawahnya
  selectedLocationId.value = null
  selectedRoomId.value = null
  locationOptions.value = []
  roomOptions.value = []

  loading.value = true
  try {
    // Panggil API untuk mendapatkan lokasi berdasarkan customer
    const { locations } = await getLocationByCustomerId(customerId)
    console.log
    locationOptions.value = locations.map(l => ({ value: l.id, label: l.name }))
  } catch (error) {
    notification.error({ message: 'Gagal memuat data lokasi' })
  } finally {
    loading.value = false
  }
}

const handleLocationChange = async locationId => {
  console.log(locationId, 'cekini')
  // Reset pilihan di bawahnya
  selectedRoomId.value = null
  roomOptions.value = []

  loading.value = true
  try {
    // Panggil API untuk mendapatkan ruangan berdasarkan lokasi
    const { rooms } = await getRoomByCustomerId(selectedCustomerId.value, locationId)
    roomOptions.value = rooms.map(r => ({ value: r.id, label: r.name }))
  } catch (error) {
    notification.error({ message: 'Gagal memuat data ruangan' })
  } finally {
    loading.value = false
  }
}

const filterOption = (input, option) => {
  return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
}

const downloadTemplate = () => {
  const link = document.createElement('a')
  link.href = '/Template_MODC.xlsx'
  link.setAttribute('download', 'Template_MODC.xlsx')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  notification.success({ message: 'Template sedang diunduh' })
}

const handleFile = file => {
  loading.value = true
  const reader = new FileReader()
  reader.onload = e => {
    try {
      const data = new Uint8Array(e.target.result)
      const workbook = XLSX.read(data, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]
      const json = XLSX.utils.sheet_to_json(worksheet)

      if (json.length === 0) {
        notification.warn({
          message: 'File Excel kosong',
          description: 'Pastikan file yang Anda upload memiliki data.',
        })
        return
      }

      const transformedAssets = json.map(row => {
        return {
          ...row,
          Procurement_Tanggal_Pembelian: excelSerialToDateString(row.Procurement_Tanggal_Pembelian),
          Procurement_Garansi_Mulai: excelSerialToDateString(row.Procurement_Garansi_Mulai),
          Procurement_Garansi_Berakhir: excelSerialToDateString(row.Procurement_Garansi_Berakhir),
        }
      })
      parsedAssets.value = transformedAssets
      loading.value = false
      nextStep() // Pindah ke langkah review
    } catch (error) {
      notification.error({
        message: 'Gagal Membaca File',
        description: 'Pastikan format file Excel sudah benar.',
      })
      loading.value = false
    }
  }
  reader.readAsArrayBuffer(file)
  return false // Mencegah upload otomatis dari komponen a-upload
}

const handleSubmit = async () => {
  loading.value = true

  // 1. Bentuk payload sesuai kesepakatan
  const payload = {
    customerId: selectedCustomerId.value,
    locationId: selectedLocationId.value,
    roomId: selectedRoomId.value,
    assetSubCategoryId: selectedCategoriesAssets.value,
    manufacturerId: selectedManufatureId.value,
    vendorId: selectedVendorId.value,
    maintenanceCycleId: selectedCycleId.value,
    assets: parsedAssets.value,
  }
  console.log(payload)

  try {
    // 2. Kirim ke API
    const response = await postAssetsBulks(payload)
    // 3. Tampilkan hasil
    notification.success({
      message: 'Upload Berhasil',
      description: `${response.message}`,
    })
    // Reset ke awal setelah berhasil
    currentStep.value = 0
    selectedCustomerId.value = null
    selectedLocationId.value = null
    selectedRoomId.value = null
    parsedAssets.value = []
    loading.value = false
  } catch (error) {
    console.log(error)
    notification.error({
      message: 'Upload Gagal',
      // Menampilkan laporan error dari backend jika ada
      description: error.response?.data?.message || 'Terjadi kesalahan pada server.',
    })
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.step-content {
  margin-top: 24px;
  padding: 24px;
  border: 1px dashed #e9e9e9;
  border-radius: 6px;
  background-color: #fafafa;
  min-height: 200px;
}
.w-100 {
  width: 100%;
}
.text-subtitle {
  font-size: 1.3rem;
  font-weight: 500;
  margin-bottom: 16px;
}
.text-label {
  margin-bottom: 8px;
}
.mb-4 {
  margin-bottom: 1.5rem;
}
.mb-5 {
  margin-bottom: 2rem;
}
</style>
